import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    ActionPermissionsObject,
    IWorkspaceUserGroup,
    PermissionActionType,
    WorkspaceUserGroupType,
    WorkspacePlanTier
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { Dropdown } from "js/Components/Dropdown";
import { MenuItem } from "js/Components/Menu";
import { ToolTipBadge } from "js/Components/ToolTip";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { trackActivity } from "js/core/utilities/utilities";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexBox } from "js/react/components/LayoutGrid";
import { Gap20 } from "js/react/components/Gap";
import { TeamResourcesPaneHeader } from "js/react/views/TeamResources/components/TeamResourcesPaneHeader";
import { TeamResourcesResultsContainer, TeamResourcesUIPane } from "js/react/views/TeamResources/components/TeamResourcesPaneLayout";
import { t } from "js/i18n";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@material-ui/icons";

const logger = getLogger(LogGroup.WORKSPACES);

enum BrandingPermission {
    ALL = "all",
    OWNER_AND_LIB = "ownerAndLib",
    NONE = "none",
}

const selectOptions = [
    {
        label: "All Members",
        value: BrandingPermission.ALL,
    },
    {
        label: "Librarians and Owners Only",
        value: BrandingPermission.OWNER_AND_LIB,
    },
    {
        label: "Nobody in this Workspace",
        value: BrandingPermission.NONE,
    },
];

interface LegacyBrandingPermission {
    inspirationSlides: BrandingPermission,
    customThemes: BrandingPermission,
    stockLibraries: BrandingPermission,
    webImages: BrandingPermission,
    aiImages: BrandingPermission,
    classicSlides: BrandingPermission,
    convertToClassic: BrandingPermission,
    importPPT: BrandingPermission,
    exportPresentation: BrandingPermission,
}

const Container = styled.div<{ disabled: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ disabled }) => disabled && `
        opacity: 0.5;
    `}
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin-bottom: 10px;

    >div {
        width: 90px;
    }
`;

const GuardrailContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 20px 16px;
    background: #fff;
    position: relative;
    border-bottom: 1px solid #D9D9D9;
`;

const GuardrailLabel = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

const GuardrailLabelText = styled.div`
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: -0.32px;
`;

const GuardrailDropdown = styled(Dropdown).attrs({ IconComponent: KeyboardArrowDownIcon })`
    &&& {
        border: none;

        .MuiSelect-select {
            font-family: "Source Sans Pro";
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 125%;
            padding-right: 30px;
        }

        .MuiSelect-icon {
            right: 0;
        }

        .MuiSvgIcon-root {            
            color: black;
        }

        fieldset {
            border: none;
        }
    }
`;

const GuardrailTooltipBadge = styled.div`
    && .tooltip-badge {
        border-color: #000;
        color: #000;
    }

   .tooltip-badge .bai-icon {
        color: #000 !important;
    }
`;

function Guardrail(props: {
    onChange: (value: BrandingPermission) => void;
    label: string;
    tooltip: string;
    value: BrandingPermission;
    disabled: boolean;
    fetching: boolean;
    "data-test-id"?: string;
}) {
    const {
        onChange,
        label,
        tooltip,
        value,
        disabled,
        fetching,
        "data-test-id": dataTestId
    } = props;
    const handleSelect = (selectedValue: BrandingPermission) => {
        if (value === selectedValue) {
            return;
        }

        onChange(selectedValue);
    };

    return (
        <GuardrailContainer data-test-id={dataTestId}>
            <FetchingClickShield visible={fetching} backgroundColor="rgba(255,255,255,0.5)" />
            <GuardrailLabel>
                <GuardrailLabelText>{t(label)}</GuardrailLabelText>
                <GuardrailTooltipBadge>
                    <ToolTipBadge>{t(tooltip)}</ToolTipBadge>
                </GuardrailTooltipBadge>
            </GuardrailLabel>
            <GuardrailDropdown value={value} onChange={handleSelect} disabled={disabled}>
                {selectOptions.map(option => (
                    <MenuItem value={option.value} key={option.value} data-test-id={option.value}>
                        {t(option.label)}
                    </MenuItem>
                ))}
            </GuardrailDropdown>
        </GuardrailContainer>
    );
}

// Since we have to preserve the outer looks of the UI which matches the old data shape,
// we have to do this weird back and forth data transformation...
function getBrandingPermissions(userGroups: IWorkspaceUserGroup[]): LegacyBrandingPermission {
    const allMembersGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.MEMBERS);
    const librariansGroup = userGroups.find(group => group.type === WorkspaceUserGroupType.LIBRARIANS);

    const getPermission = (object: ActionPermissionsObject, action: PermissionActionType) =>
        allMembersGroup.actionPermissions[object][action]
            ? BrandingPermission.ALL
            : librariansGroup.actionPermissions[object][action]
                ? BrandingPermission.OWNER_AND_LIB
                : BrandingPermission.NONE;

    return {
        inspirationSlides: getPermission(ActionPermissionsObject.INSPIRATION_SLIDES, PermissionActionType.USE),
        customThemes: getPermission(ActionPermissionsObject.CUSTOM_THEMES, PermissionActionType.USE),
        stockLibraries: getPermission(ActionPermissionsObject.STOCK_LIBRARIES, PermissionActionType.USE),
        webImages: getPermission(ActionPermissionsObject.WEB_IMAGES, PermissionActionType.USE),
        aiImages: getPermission(ActionPermissionsObject.GENERATIVE_AI_IMAGES, PermissionActionType.USE),
        classicSlides: getPermission(ActionPermissionsObject.CLASSIC_SLIDES, PermissionActionType.USE),
        convertToClassic: getPermission(ActionPermissionsObject.CONVERT_TO_CLASSIC, PermissionActionType.USE),
        importPPT: getPermission(ActionPermissionsObject.IMPORT_PPT, PermissionActionType.USE),
        exportPresentation: getPermission(ActionPermissionsObject.EXPORT_PRESENTATION, PermissionActionType.USE)
    };
}

function getGroupUpdates(object: ActionPermissionsObject, action: PermissionActionType, value: BrandingPermission) {
    const guestsGroup = WorkspaceController.userGroups.find(group => group.type === WorkspaceUserGroupType.GUESTS);
    const membersGroup = WorkspaceController.userGroups.find(group => group.type === WorkspaceUserGroupType.MEMBERS);
    const librariansGroup = WorkspaceController.userGroups.find(group => group.type === WorkspaceUserGroupType.LIBRARIANS);
    const ownersGroup = WorkspaceController.userGroups.find(group => group.type === WorkspaceUserGroupType.OWNERS);

    return {
        [guestsGroup.id]: {
            actionPermissions: {
                [object]: {
                    [action]: value === BrandingPermission.ALL,
                },
            },
        },
        [membersGroup.id]: {
            actionPermissions: {
                [object]: {
                    [action]: value === BrandingPermission.ALL,
                },
            },
        },
        [librariansGroup.id]: {
            actionPermissions: {
                [object]: {
                    [action]: value === BrandingPermission.OWNER_AND_LIB || value === BrandingPermission.ALL,
                },
            },
        },
        [ownersGroup.id]: {
            actionPermissions: {
                [object]: {
                    [action]: value === BrandingPermission.OWNER_AND_LIB || value === BrandingPermission.ALL,
                },
            },
        },
    };
}

export default WorkspaceController.withInitializedState(function TeamBrandingGuardrailsPane(props: WorkspaceControllerState) {
    const { userGroups } = props;
    const [fetchingField, setFetchingField] = useState<string | null>(null);
    const [brandingPermissions, setBrandingPermissions] = useState<LegacyBrandingPermission>(getBrandingPermissions(userGroups));

    const canUpgradeToManageBrandGuardrails = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.BRAND_GUARDRAILS, PermissionActionType.MANAGE);

    useEffect(() => {
        setBrandingPermissions(getBrandingPermissions(userGroups));
    }, [getObjectHash(userGroups)]);

    const handleChange = async (field: string, value: BrandingPermission) => {
        const updatedBrandingPermissions = {
            ...brandingPermissions,
            [field]: value,
        };
        setBrandingPermissions(updatedBrandingPermissions);

        trackActivity("TeamGuardrails", "GuardChange", null, null, {
            guard: field,
            permission: value,
        }, { audit: false });

        let groupUpdates: { [groupId: string]: { actionPermissions: Partial<IWorkspaceUserGroup["actionPermissions"]> } } = {};

        if (field === "inspirationSlides") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.INSPIRATION_SLIDES, PermissionActionType.USE, value);
        } else if (field === "customThemes") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.CUSTOM_THEMES, PermissionActionType.USE, value);
        } else if (field === "stockLibraries") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.STOCK_LIBRARIES, PermissionActionType.USE, value);
        } else if (field === "webImages") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.WEB_IMAGES, PermissionActionType.USE, value);
        } else if (field === "aiImages") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.GENERATIVE_AI_IMAGES, PermissionActionType.USE, value);
        } else if (field === "classicSlides") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.CLASSIC_SLIDES, PermissionActionType.USE, value);
        } else if (field === "convertToClassic") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.CONVERT_TO_CLASSIC, PermissionActionType.USE, value);
        } else if (field === "importPPT") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.IMPORT_PPT, PermissionActionType.USE, value);
        } else if (field === "exportPresentation") {
            groupUpdates = getGroupUpdates(ActionPermissionsObject.EXPORT_PRESENTATION, PermissionActionType.USE, value);
        }

        setFetchingField(field);
        try {
            await WorkspaceController.updateWorkspaceUserGroups({ update: groupUpdates });
        } catch (err) {
            logger.error(err, "Failed to update branding permissions");
            ShowErrorDialog({
                title: t("Failed to update brand guardrails"),
                message: t("An error occurred while updating the brand guardrails. Please try again later."),
            });
        } finally {
            setFetchingField(null);
        }
    };

    const {
        inspirationSlides,
        customThemes,
        stockLibraries,
        webImages,
        aiImages,
        classicSlides,
        convertToClassic,
        importPPT,
        exportPresentation
    } = brandingPermissions;

    return (
        <TeamResourcesUIPane>
            <TeamResourcesPaneHeader title={t("Brand Guardrails")} />
            {canUpgradeToManageBrandGuardrails && (
                <FlexBox center fillWidth padding="0 0 20px">
                    <Banner
                        headerText={t("Unlock Brand Guardrails")}
                        bodyText={t("Upgrade to a Team plan to safeguard your brand with advanced controls and consistency.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "brand_guardrails" }
                            })}
                    />
                </FlexBox>
            )}
            <TeamResourcesResultsContainer>
                <Container disabled={canUpgradeToManageBrandGuardrails}>
                    {canUpgradeToManageBrandGuardrails && <Gap20 />}
                    <HeaderContainer>
                        <div>{t("Feature")}</div>
                        <div>{t("Access")}</div>
                    </HeaderContainer>
                    <Guardrail
                        label="Built-in Starter Templates and Inspiration Slides"
                        tooltip="Decide who on your team has access to our pre-designed starter materials."
                        value={inspirationSlides}
                        onChange={value => handleChange("inspirationSlides", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "inspirationSlides"}
                        data-test-id="inspirationSlides"
                    />
                    <Guardrail
                        label="Built-in and Custom Themes"
                        tooltip="Decide who on your team has access to themes outside of your Team Theme."
                        value={customThemes}
                        onChange={value => handleChange("customThemes", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "customThemes"}
                        data-test-id="customThemes"
                    />
                    <Guardrail
                        label="Stock Image and Video Libraries"
                        tooltip="Decide who on your team has access to our stock media library, in addition to Team Assets, icons, logos and media upload."
                        value={stockLibraries}
                        onChange={value => handleChange("stockLibraries", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "stockLibraries"}
                        data-test-id="stockLibraries"
                    />
                    <Guardrail
                        label="Web Images"
                        tooltip="Decide who on your team has access to our web image search, in addition to Team Assets, icons, logos, and media upload."
                        value={webImages}
                        onChange={value => handleChange("webImages", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "webImages"}
                        data-test-id="webImages"
                    />
                    {WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use &&
                        <Guardrail
                            label="AI Generated Images"
                            tooltip="Decide who on your team can use AI to generate images."
                            value={aiImages}
                            onChange={value => handleChange("aiImages", value)}
                            disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                            fetching={fetchingField === "aiImages"}
                            data-test-id="aiImages"
                        />
                    }
                    <Guardrail
                        label="Classic Slides"
                        tooltip="Decide who on your team can design a slide from scratch with our blank, Classic slide."
                        value={classicSlides}
                        onChange={value => handleChange("classicSlides", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "classicSlides"}
                        data-test-id="classicSlides"
                    />
                    <Guardrail
                        label="Convert to Classic"
                        tooltip="Decide who on your team can remove design guardrails by converting Smart Slides to Classic."
                        value={convertToClassic}
                        onChange={value => handleChange("convertToClassic", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "convertToClassic"}
                        data-test-id="convertToClassic"
                    />
                    <Guardrail
                        label="PowerPoint Import"
                        tooltip="Decide who on your team can import PowerPoint slides into presentations."
                        value={importPPT}
                        onChange={value => handleChange("importPPT", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "importPPT"}
                        data-test-id="importPPT"
                    />
                    <Guardrail
                        label="Export"
                        tooltip="Decide who on your team can export slides to PowerPoint, Google Slides, PDF or as an image. If export is restricted, presentations can only be shared via tracked link, email or embed."
                        value={exportPresentation}
                        onChange={value => handleChange("exportPresentation", value)}
                        disabled={!!fetchingField || canUpgradeToManageBrandGuardrails}
                        fetching={fetchingField === "exportPresentation"}
                        data-test-id="exportPresentation"
                    />
                </Container>
            </TeamResourcesResultsContainer>
        </TeamResourcesUIPane>
    );
});
